import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Camera, AlertCircle, Loader2, ScanBarcode } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import {
  BarcodeScanner,
  BarcodeFormat,
} from '@capacitor-mlkit/barcode-scanning';
import { Button } from '@/components/ui';
import { isNative } from '@/lib/capacitor';

interface NativeBarcodeScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onScan: (barcode: string) => void;
}

type ScannerState = 'idle' | 'scanning' | 'permission_denied' | 'not_supported' | 'error';

export function NativeBarcodeScannerModal({
  isOpen,
  onClose,
  onScan,
}: NativeBarcodeScannerModalProps) {
  const { t } = useTranslation('scanner');
  const [scannerState, setScannerState] = useState<ScannerState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const startScanner = useCallback(async () => {
    if (!isNative) {
      setScannerState('not_supported');
      setErrorMessage(t('barcodeScanner.notNative'));
      return;
    }

    try {
      setScannerState('scanning');
      setErrorMessage(null);

      // Check if scanning is supported
      const { supported } = await BarcodeScanner.isSupported();
      if (!supported) {
        setScannerState('not_supported');
        setErrorMessage(t('barcodeScanner.notSupported'));
        return;
      }

      // Check/request camera permissions
      let permissionStatus = await BarcodeScanner.checkPermissions();

      if (permissionStatus.camera === 'prompt' || permissionStatus.camera === 'prompt-with-rationale') {
        permissionStatus = await BarcodeScanner.requestPermissions();
      }

      if (permissionStatus.camera !== 'granted') {
        setScannerState('permission_denied');
        setErrorMessage(t('barcodeScanner.permissionDenied'));
        return;
      }

      // Use scan() method which opens native scanner UI
      const { barcodes } = await BarcodeScanner.scan({
        formats: [
          BarcodeFormat.Ean13,
          BarcodeFormat.Ean8,
          BarcodeFormat.UpcA,
          BarcodeFormat.UpcE,
          BarcodeFormat.Code128,
          BarcodeFormat.Code39,
        ],
      });

      setScannerState('idle');

      if (barcodes.length > 0 && barcodes[0].rawValue) {
        onScan(barcodes[0].rawValue);
        onClose();
      }
    } catch (error) {
      console.error('Scanner error:', error);

      // Check if user cancelled
      if (error instanceof Error && error.message.includes('canceled')) {
        setScannerState('idle');
        return;
      }

      setScannerState('error');
      setErrorMessage(
        error instanceof Error ? error.message : t('barcodeScanner.unknownError')
      );
    }
  }, [onScan, onClose]);

  const handleClose = useCallback(() => {
    setScannerState('idle');
    setErrorMessage(null);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={handleClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal bg-pp-dark">
          {/* Header */}
          <div className="bottom-sheet-header">
            <h2>{t('barcodeScanner.title')}</h2>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content flex flex-col items-center justify-center min-h-[250px] text-center">
            {scannerState === 'idle' && (
              <>
                <div className="w-20 h-20 rounded-full bg-pp-orange/20 flex items-center justify-center mb-6">
                  <ScanBarcode className="w-10 h-10 text-pp-orange" />
                </div>
                <h3 className="text-white font-medium mb-2">{t('barcodeScanner.ready')}</h3>
                <p className="text-white/60 text-sm mb-6 max-w-[280px]">
                  {t('barcodeScanner.readyHint')}
                </p>
                <Button
                  onClick={startScanner}
                  className="bg-pp-orange hover:bg-pp-orange/90 text-white px-8"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  {t('barcodeScanner.openScanner')}
                </Button>
              </>
            )}

            {scannerState === 'scanning' && (
              <>
                <Loader2 className="w-12 h-12 text-pp-orange animate-spin mb-4" />
                <p className="text-white">{t('barcodeScanner.opening')}</p>
              </>
            )}

            {scannerState === 'permission_denied' && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                  <Camera className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-white font-medium mb-2">{t('barcodeScanner.permissionRequired')}</h3>
                <p className="text-white/60 text-sm mb-4 max-w-[280px]">{errorMessage}</p>
                <Button variant="secondary" onClick={startScanner} className="bg-white/20 text-white">
                  {t('barcodeScanner.retry')}
                </Button>
              </>
            )}

            {scannerState === 'not_supported' && (
              <>
                <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center mb-4">
                  <AlertCircle className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-white font-medium mb-2">{t('barcodeScanner.notAvailable')}</h3>
                <p className="text-white/60 text-sm max-w-[280px]">{errorMessage}</p>
              </>
            )}

            {scannerState === 'error' && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                  <AlertCircle className="w-8 h-8 text-red-400" />
                </div>
                <h3 className="text-white font-medium mb-2">{t('barcodeScanner.error')}</h3>
                <p className="text-white/60 text-sm mb-4 max-w-[280px]">{errorMessage}</p>
                <Button variant="secondary" onClick={startScanner} className="bg-white/20 text-white">
                  {t('barcodeScanner.retry')}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
